import { createContext, useContext } from 'react';

import type { CommentAgentSessionMap } from './usePageCommentAgentSessions.ts';

/**
 * React context that holds the page-level agent session map fetched by
 * usePageCommentAgentSessions. Provided once at the ObjectCommentContainer
 * level and consumed per-comment in ObjectCommentThread / ObjectComment.
 *
 * Map shape: Map<commentId, { conversationId, state }> where commentId is the
 * numeric ID extracted from the subEntityId ARI (e.g. '111116289'), not the full ARI string.
 *
 * Default is an empty Map — consumers must be mounted inside the provider
 * to get real data.
 */
export const AgentSessionsContext = createContext<CommentAgentSessionMap>(new Map());

/**
 * Hook to consume the agent sessions map from context.
 *
 * @example
 * ```tsx
 * const sessions = useAgentSessionsContext();
 * const session = sessions.get(commentId); // numeric comment ID, e.g. comment.id
 * // session?.conversationId, session?.state
 * ```
 */
export function useAgentSessionsContext(): CommentAgentSessionMap {
	return useContext(AgentSessionsContext);
}
